import { useMemo } from 'react';
import type { SWRConfiguration } from 'swr';
import type {
  InteractionDocument,
  InteractionDocumentsResponse,
} from '@/common/types/interaction';
import { useCore } from '$api-clients/hooks';

interface Options extends SWRConfiguration<InteractionDocumentsResponse> {
  skip?: boolean;
}

// The server sorts by a single column with no tie-breaker, and duplicate
// envelopeOrder values are common (legacy rows defaulted to 0), so ties come
// back in nondeterministic order. Break them client-side for a stable display.
const compareDocuments = (a: InteractionDocument, b: InteractionDocument) =>
  a.envelopeOrder - b.envelopeOrder ||
  a.createdAt.localeCompare(b.createdAt) ||
  a.id.localeCompare(b.id);

/**
 * The sole entry point for fetching an interaction's document list.
 *
 * Every consumer must share this exact query shape so all mounts of the same
 * interaction collapse into a single SWR key — one request, one cache entry,
 * one error-retry timer. Distinct keys multiply error retries into request
 * storms during outages. Consumers needing a different order or subset
 * derive it client-side from `documents`.
 */
export const useInteractionDocuments = (
  interactionId: string | undefined,
  { skip, ...swrConfig }: Options = {},
) => {
  const { data, isLoading, error, isRetrying, isTerminalError, mutate } =
    useCore(
      '/interactions/{interactionId}/documents',
      skip || !interactionId
        ? null
        : {
            params: {
              path: { interactionId },
              // Endpoint is paginated, but we want all results in a single request.
              query: { limit: 1000, sort: 'envelopeOrder:asc' },
            },
          },
      swrConfig,
    );

  const documents = useMemo(
    () => [...(data?.results ?? [])].sort(compareDocuments),
    [data],
  );

  return {
    documents,
    data,
    isLoading,
    error,
    isRetrying,
    isTerminalError,
    mutate,
  };
};
